/**
 * ConnectTwitterButton
 * 
 * Button component to initiate Twitter OAuth flow.
 */

import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Twitter/X SVG Icon
const TwitterIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export default function ConnectTwitterButton({ variant = 'default', className = '' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleConnect = async () => {
    setLoading(true);
    setError(null);

    try {
      // Call backend to get authorization URL (cookies sent automatically)
      const response = await fetchWithAuth(
        '/api/auth/twitter/start',
        {
          method: 'POST',
          body: JSON.stringify({})
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start OAuth flow');
      }

      const data = await response.json();

      if (data.success) {
        const { authorization_url, state, code_verifier } = data.data;
        
        // Store PKCE code_verifier temporarily (for callback)
        sessionStorage.setItem(`twitter_pkce_${state}`, code_verifier);
        
        // Redirect to Twitter authorization page
        window.location.href = authorization_url;
      } else {
        setError(data.message || 'Failed to start OAuth flow');
        setLoading(false);
      }
    } catch (err) {
      console.error('Connect Twitter error:', err);
      setError(err.message || 'Failed to connect. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button
        onClick={handleConnect}
        disabled={loading}
        variant={variant}
        className={`w-full ${className}`}
        data-testid="connect-twitter-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Connexion...
          </>
        ) : (
          <>
            <TwitterIcon className="w-4 h-4 mr-2" />
            Connecter Twitter
          </>
        )}
      </Button>
      
      {error && (
        <p className="text-sm text-red-500" data-testid="connect-twitter-error">
          {error}
        </p>
      )}
    </div>
  );
}
